import { isApiError } from '@/services/api';

const MONEY_2DP = /^-?\d+\.\d{2}$/;
const CURRENCY = /^[A-Z]{3}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const UNVERIFIED_CODE = 'ledger_statement_unverified';
export const NOT_ENTITLED_CODE = 'fund_statement_not_entitled';

export const hasApiCode = (error: unknown, status: number, code: string): boolean =>
    isApiError(error) && error.status === status && error.code === code;

export const payloadReaders = (label: string) => {
    const malformed = (detail: string): Error => new Error(`Malformed ${label}: ${detail}`);

    const readRecord = (value: unknown, name: string): Record<string, unknown> => {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
            throw malformed(`${name} is not an object`);
        }
        return value as Record<string, unknown>;
    };

    const readText = (value: unknown, name: string): string => {
        if (typeof value !== 'string' || value === '') throw malformed(`${name} is not a non-empty string`);
        return value;
    };

    const readNullableText = (value: unknown, name: string): string | null =>
        value === undefined || value === null ? null : readText(value, name);

    const readMoney = (value: unknown, name: string): string => {
        if (typeof value !== 'string' || !MONEY_2DP.test(value)) throw malformed(`${name} is not a 2dp amount`);
        return value;
    };

    const readInt = (value: unknown, name: string, min: number): number => {
        if (typeof value !== 'number' || !Number.isInteger(value) || value < min) {
            throw malformed(`${name} is not an integer of at least ${min}`);
        }
        return value;
    };

    const readCurrency = (value: unknown): string => {
        const text = readText(value, 'currency');
        if (!CURRENCY.test(text)) throw malformed('currency is not a 3-letter code');
        return text;
    };

    const readIsoDate = (value: unknown, name: string): string => {
        const text = readText(value, name);
        if (!ISO_DATE.test(text)) throw malformed(`${name} is not a Y-m-d date`);
        return text;
    };

    const readRealDate = (value: unknown, name: string): string => {
        const text = readIsoDate(value, name);
        const [year, month, day] = text.split('-').map(Number);
        const parsed = new Date(Date.UTC(year, month - 1, day));
        if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
            throw malformed(`${name} is not a real date`);
        }
        return text;
    };

    const readArray = (value: unknown, name: string): unknown[] => {
        if (!Array.isArray(value)) throw malformed(`${name} is not an array`);
        return value;
    };

    return { malformed, readRecord, readText, readNullableText, readMoney, readInt, readCurrency, readIsoDate, readRealDate, readArray };
};
